import React from "react";
import styled from "styled-components";
import { ScheduledPost } from "../../../../../database/community/scheduledPosts";
import { platformLabel } from "../../publish/SchedulePostDialog";
import { useMediaStudio } from "../../context/MediaStudioContext";
import { CardHint, Muted, Row, RowList, StatusDot, Thumb, STATUS_COLOR, projectThumb } from "../shared";
import { StatKey } from "./StatStrip";
import {
  compact,
  postEngagement,
  postViews,
  sentWithMetrics,
  timeLabel,
  weekPosts,
} from "./homeDerive";

// Drill-down for a stat tile: the actual posts behind the number, each row
// clickable through to the post detail panel.

const ClickRow = styled(Row)`
  cursor: pointer;

  &:hover {
    background: var(--color-surface-subtle);
  }
`;

const Caption = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const STAT_TITLES: Record<StatKey, string> = {
  views: "Views by post",
  avg: "Views by post",
  engagement: "Engagement by post",
  week: "This week's posts",
};

export type StatDetailPanelProps = {
  statKey: StatKey;
  onSelectPost: (postId: string) => void;
};

// Body of the stat drill-down modal: the actual posts behind the number.
export const StatDetailPanel: React.FC<StatDetailPanelProps> = ({
  statKey,
  onSelectPost,
}) => {
  const { posts, projects } = useMediaStudio();

  const thumbFor = (p: ScheduledPost) =>
    projectThumb(p.projectId ? projects.projectById.get(p.projectId) : undefined) ||
    p.mediaUrls[0];

  const renderRow = (p: ScheduledPost, right: React.ReactNode) => {
    const thumb = thumbFor(p);
    return (
      <ClickRow key={p.id} onClick={() => onSelectPost(p.id)}>
        <StatusDot $color={STATUS_COLOR[p.status]} />
        {thumb ? <Thumb src={thumb} alt="" /> : null}
        <div style={{ minWidth: 0, flex: 1 }}>
          <Caption>{p.caption?.trim() || "Untitled post"}</Caption>
          <Muted>
            {timeLabel(p.dueAt)} · {p.platforms.map(platformLabel).join(", ")}
          </Muted>
        </div>
        <Muted>{right}</Muted>
      </ClickRow>
    );
  };

  let body: React.ReactNode;
  if (statKey === "week") {
    const week = weekPosts(posts.posts);
    body = (
      <>
        {week.posted.length + week.upcoming.length === 0 ? (
          <CardHint>Nothing posted or scheduled this week yet.</CardHint>
        ) : (
          <RowList>
            {week.posted.map((p) =>
              renderRow(p, postViews(p) > 0 ? `${compact(postViews(p))} views` : "posted")
            )}
            {week.upcoming.map((p) =>
              renderRow(p, p.status === "draft" ? "draft" : "queued")
            )}
          </RowList>
        )}
      </>
    );
  } else {
    const measured = sentWithMetrics(posts.posts).sort(
      (a, b) => postViews(b) - postViews(a)
    );
    body =
      measured.length === 0 ? (
        <CardHint>
          No measured posts yet. Metrics arrive after Instagram or Facebook
          posts publish.
        </CardHint>
      ) : (
        <RowList>
          {measured.map((p) => {
            if (statKey === "engagement") {
              const e = postEngagement(p);
              return renderRow(
                p,
                `${compact(e.likes)} likes · ${compact(e.comments)} comments`
              );
            }
            return renderRow(p, `${compact(postViews(p))} views`);
          })}
        </RowList>
      );
  }

  return (
    <>
      {body}
      <CardHint>Click a post for its metrics and public link.</CardHint>
    </>
  );
};

export default StatDetailPanel;
